import { Box, Grid, Typography, useTheme } from "@mui/material";
import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Label,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { groupByDateSplit } from "./_common/Analytics";
import SectionCard from "@/app/admin/_common/SectionCard";
import { BarChartSkeleton } from "./HomeDashboardSkeletons";

interface PastWeekSignupsProps {
  isLoading: boolean;
  guests: any;
  hideSubtitle?: boolean;
  compact?: boolean;
}

function SignupsTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        backgroundColor: "var(--cuo-bg-elevated, #fff)",
        border: "1px solid var(--cuo-border-color)",
        borderRadius: "8px",
        padding: "10px 12px",
        minWidth: 160,
      }}
    >
      <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#000", mb: 0.75 }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", rowGap: "6px" }}>
        {payload.map((entry: any) => (
          <Box
            key={entry.dataKey}
            sx={{ display: "flex", alignItems: "center", columnGap: "8px" }}
          >
            <Box
              sx={{
                width: 9,
                height: 9,
                borderRadius: "2px",
                backgroundColor: entry.color,
                flexShrink: 0,
              }}
            />
            <Typography sx={{ fontSize: "13px", color: "#000", whiteSpace: "nowrap" }}>
              {entry.name}
            </Typography>
            <Box
              sx={{
                flex: 1,
                borderBottom: "1px dotted var(--cuo-border-color)",
                marginBottom: "2px",
              }}
            />
            <Typography sx={{ fontSize: "13px", color: "#000" }}>{entry.value}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function PastWeekSignupsCard(props: PastWeekSignupsProps) {
  const { isLoading, guests, hideSubtitle, compact } = props;
  return (
    <SectionCard
      isLoading={isLoading}
      loadingContent={<BarChartSkeleton />}
      flex={1}
      sx={{ flex: { xs: "0 0 auto", md: 1 } }}
      label={
        <Grid container direction={"column"} rowGap={0.25}>
          <Typography sx={{ color: "var(--cuo-text)", fontSize: "15px", fontWeight: 600 }}>
            Waitlist signups
          </Typography>
          {!hideSubtitle && (
            <Typography sx={{ color: "var(--cuo-text-muted)", fontSize: "13px" }}>
              Daily new signups this week
            </Typography>
          )}
        </Grid>
      }
      rightItems={
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box
              sx={{
                width: 9,
                height: 9,
                borderRadius: "2px",
                backgroundColor: "#1e3a8a",
                flexShrink: 0,
              }}
            />
            <Typography sx={{ fontSize: "13px", color: "var(--cuo-text)" }}>
              Repeat
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box
              sx={{
                width: 9,
                height: 9,
                borderRadius: "2px",
                backgroundColor: "#4BB543",
                flexShrink: 0,
              }}
            />
            <Typography sx={{ fontSize: "13px", color: "var(--cuo-text)" }}>
              New
            </Typography>
          </Box>
        </Box>
      }
    >
      {!isLoading && (
        <Grid
          container
          alignContent={"end"}
          p={1}
          pb={0}
          sx={{
            flex: { xs: "0 0 auto", md: 1 },
            height: { xs: 140, md: "100%" },
            minHeight: 0,
            fontFamily: "sans-serif !important",
            ".recharts-rectangle.recharts-tooltip-cursor": {
              fill: "var(--cuo-bg-hover) !important",
            },
            text: {
              fontSize: "10px !important",
              fill: "var(--cuo-text) !important",
            },
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={groupByDateSplit(guests.data, "signup_date")}>
              <Tooltip content={<SignupsTooltip />} />
              <CartesianGrid
                stroke="var(--cuo-border-soft-color)"
                horizontal={true}
                vertical={false}
                z={0}
              />
              <Bar
                dataKey="signups"
                fill={"#1e3a8a"}
                name="Repeat"
                radius={[1, 1, 0, 0]}
                z={1}
                barSize={12}
              />
              <Bar
                dataKey="newSignups"
                fill={"#4BB543"}
                name="New"
                radius={[1, 1, 0, 0]}
                z={1}
                barSize={12}
              />
              <YAxis width={20} stroke="var(--cuo-border-color)" tickCount={compact ? 3 : undefined} />
              <XAxis
                dataKey="name"
                padding={"no-gap"}
                fontFamily="sans-serif"
                stroke="var(--cuo-border-color)"
              />
            </BarChart>
          </ResponsiveContainer>
        </Grid>
      )}
    </SectionCard>
  );
}
